import { FC, useRef } from "react";

import { useForm } from "react-hook-form";

import { trpc } from "../../../../../common/helpers/trpc";
import { useTranslate } from "../../../../../common/hooks/useTranslate";
import { useLiveFormAmplitudeEvents } from "../../common/amplitude/useLiveFormAmplitudeEvents";
import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";
import { useUpdateTicketingDataInPreview } from "../../common/useUpdateLiveTicketingPreview/useUpdateTicketingDataInPreview";
import { useFormSaveAndNavigate } from "../useFormSaveAndNavigate";

import { LiveFormEditorV2StepLogoStateless, StepLogoFormData } from "./LiveFormEditorV2StepLogo.stateless";

import { ImageUploaderHandle } from "@/components/ImageUploader/types";

export const TicketingLiveEditorStepLogo: FC = () => {
  const { t } = useTranslate();
  const { apiFormObject: apiTicketing } = useLiveFormEditorContext();
  const { liveFormAmplitudePayload } = useLiveFormAmplitudeEvents();

  const logoRef = useRef<ImageUploaderHandle>(null);

  const defaultValues: StepLogoFormData = {
    logo: apiTicketing.logoUrl || null,
    isUploading: false,
  };
  const form = useForm<StepLogoFormData>({ defaultValues });

  const { mutateAsync: updateTicketingLogo } = trpc.updateTicketingLogo.useMutation();
  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepLogoFormData>({
    type: "ticketing",
    form,
    saveData: async (formData) => {
      const finalLogoUrl = (await logoRef.current?.finalizeSquareCrop()) ?? formData.logo;

      return await updateTicketingLogo({
        ticketingId: apiTicketing.id,
        logoUrl: finalLogoUrl,
      });
    },
    errorMessage: t("dashboard", "liveFormEditor.stepLogo.error"),
  });

  useUpdateTicketingDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      liveDataToMutate.ticketing.logoUrl = formData.logo;

      if (!formData.logo) {
        liveDataToMutate.placeholders.isLogo = true;
      }
    },
  });

  return (
    <LiveFormEditorV2StepLogoStateless
      form={form}
      handleSaveAndNext={handleSaveAndNext}
      handleSaveAndBack={handleSaveAndBack}
      amplitudePayload={liveFormAmplitudePayload}
      logoRef={logoRef}
    />
  );
};
